import { Component, Input, ElementRef, forwardRef, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NmInput } from '../input/input';
import { NmSelectOption } from '../select/select';

declare let $: any;

export interface NmReactiveSelectOption {
	label: string,
	value: string,
	disabled?: boolean,
	visible?: boolean,
	parent?: string,
	children?: NmReactiveSelectOption[]
}

@Component({
	selector: 'nm-reactive-option',
	template: `
		<ng-container *ngFor="let option of options">
			<li
				[attr.data-value]="option.value" 
				[attr.data-disabled]="option.disabled"
				[ngClass]="{ 'hide': option.visible === false }">
				{{option.label}}
			</li>

			<nm-reactive-option *ngIf="option.children" [options]="option.children"></nm-reactive-option>
		</ng-container>
	`
})
export class NmReactiveOption {
	@Input() options: NmReactiveSelectOption[];
}

@Component({
	selector: 'nm-reactive-select',
	templateUrl: './reactive-select.html',
	styleUrls: [ './reactive-select.css' ],
	providers: [
		{
	    provide: NG_VALUE_ACCESSOR,
	    useExisting: forwardRef(() => NmReactiveSelect),
	    multi: true,
	  }
  ]
})
export class NmReactiveSelect extends NmInput {
	@Input() options: NmReactiveSelectOption[] = [];
	@Input() groups: string[] = [];
	@Input() value: string | number;
	@Output() onChanged = new EventEmitter();

	public curValue: string | number = '_';
	public curLevel: number = 0;
	public placeholder: string = '(Please Choose)';
	public isReady: boolean = false;
	private _isInit: boolean = true;
	private _labels: (string | number)[] = [];
	private _options: NmSelectOption[] = [];
	private _activeOptions: NmSelectOption[] = [];

	public ngAfterViewInit() {
		super.ngAfterViewInit();
		this._prepareValues();
	}

	public ngOnChanges() {
		this._prepareValues();
	}

	public writeValue(value: any) {
		super.writeValue(value);

		if(this.options.length == 0 || this.groups.length == 0)
			return;
		
		this._writeValue();
	}

	private _prepareValues() {
		if(!this._isInit || this.options.length == 0 || this.groups.length == 0)
			return;

		this._isInit = false;

		// ensure of array type
		if(!this._value) this._value = [];
		this._recursiveMap(this.options? this.options: [])
		.then(() => {
			this._updateActiveOptions();
			this._writeValue();
		});

		this.groups = this.groups? this.groups.map(v => v[0].toUpperCase().concat(v.slice(1).toLowerCase())): [];
	}

	// writeValue() might ran before options exist, which causes error
	private _writeValue() {
		if(this._value && this._value.length > 0) {
			this.curLevel = this._value.length - 1;
			this.curValue = this._value.slice(-1)[0];
			this._value.forEach(v => {
				this._labels.push(this._options.find(o => o.value == v).label);
			});
		}
	}

	private _recursiveMap(options: NmReactiveSelectOption[], parent = '_') {
		return new Promise((resolve) => {
			options.map(v => {
				this._options.push({
					label: v.label,
					value: v.value,
					disabled: v.disabled,
					parent: v.parent || parent
				});

				if(v.children && v.children.length > 0) {
					this._recursiveMap(v.children, v.value)
					.then(() => resolve());
				}
				else {
					resolve();
				}
			});
		});
	}

	private _updateActiveOptions() {
		this.isReady = false;

		setTimeout(() => {
			if(this.groups && this.groups.length > 0) {
				this.placeholder = `(Please Choose a ${this.groups[this.curLevel]})`;
			}

			this._activeOptions = this._options.filter((v => v.parent == this.curValue));
			this.isReady = true;
		}, 100);
	}

	public onSelected(event) {
		this.curValue = event;
		this._value.push(this.curValue);
		this._labels.push(this._options.find(v => v.value == this.curValue).label);
		this._updateActiveOptions();
		this.curLevel += 1;
		this.onInputChange(event);
	}

	public onDeleted(index) {
		this._value.splice(index);
		this._labels.splice(index);
		this.curLevel = index;
		this.curValue = this._value.slice(-1)[0] || '_';
		this._updateActiveOptions();
		this.onInputChange(event);
		this.writeValue
	}
}



// WEBPACK FOOTER //
// ./src/widgets/reactive-select/reactive-select.ts